import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "selected_tenant_id";

// Singleton axios that auto-injects the X-Tenant-ID header from localStorage
// (read on every request so it stays in sync as the user switches tenants).
// Also attaches the JWT cookie + Bearer token for the admin auth middleware.
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  const tid = localStorage.getItem(STORAGE_KEY);
  if (tid) config.headers["X-Tenant-ID"] = tid;
  const tok = localStorage.getItem("sm_token");
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

const TenantContext = createContext({
  tenantId: null, setTenantId: () => {}, tenants: [], refreshTenants: () => {},
  api, currentTenant: null,
});

export function TenantProvider({ children }) {
  const [tenantId, setTenantIdState] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const setTenantId = useCallback((id) => {
    setTenantIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const refreshTenants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/tenants");
      setTenants(r.data);
      if (r.data.length > 0 && (!tenantId || !r.data.find(t => t.id === tenantId))) {
        setTenantId(r.data[0].id);
      }
    } catch (e) {
      // 401 = not logged in. Don't blow up — admin shell will redirect to /admin/login.
      if (e.response?.status !== 401) throw e;
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, setTenantId]);

  useEffect(() => { refreshTenants(); }, []); // eslint-disable-line

  const currentTenant = tenants.find(t => t.id === tenantId) || null;

  return (
    <TenantContext.Provider value={{ tenantId, setTenantId, tenants, refreshTenants, api, currentTenant, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() { return useContext(TenantContext); }
